/**
 * `GET /admin/ai-requests` - spec 08's generation log, read-only (FR-4.4).
 *
 * This is the operational view of diet generation: what was asked, what the
 * classifier decided, what the provider did, and whether the attempt counted
 * against the member's daily quota.
 *
 * `counts_against_quota` is the field that explains a member's "you have used
 * all 3 today" when they believe they tried once: a classifier-REJECTED attempt
 * is free (`ai_requests_rejected_is_free` in the schema), while an accepted one
 * that later hit a provider error is not. The log is where that becomes visible.
 *
 * `classifier_reason` is deliberately NOT returned: for a rejection it is the
 * classifier's own verdict, so it can quote what the member typed - dietary
 * preferences or health conditions, which admins must not see.
 *
 * No prompt or response body is stored anywhere, so none can leak here.
 */

import { request } from "./client";
import type { Page } from "./types";

export type AiRequestEntry = {
  id: string;
  user_id: string;
  member_name: string | null;
  plan_id: string | null;
  request_type: "week" | "day";
  target_day_index: number | null;
  classifier_verdict: "accepted" | "rejected";
  classifier_model: string | null;
  model: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  latency_ms: number | null;
  /** `success` | `classifier_rejected` | `validation_failed` | `provider_error` | `rate_limited`. */
  outcome: string;
  error_message: string | null;
  quota_date: string;
  counts_against_quota: boolean;
  created_at: string;
};

export type AiLogFilters = {
  user_id?: string;
  outcome?: string;
  limit?: number;
  offset?: number;
};

export function fetchAiRequests(filters: AiLogFilters): Promise<Page<AiRequestEntry>> {
  return request<Page<AiRequestEntry>>("/admin/ai-requests", { query: { ...filters } });
}
